import React, { useState, useRef, useEffect } from 'react'
import ReactDOM from 'react-dom'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import './ImageViewerFolderListStyle.css'
import styled from 'styled-components'
import {
  renameFolderAction,
  deleteFolder,
  fetchFolderById,
} from '@store/actions/folder.action'
import { formatBytesToMB } from '@utils/imageViewer.utils'
import { Dropdown } from 'react-bootstrap'
import RenameFolderModal from './RenameFolderModal.component'
import ConfirmBlockModal from '@components/layout/Modals/ConfirmBlockModal/ConfirmBlockModal.component'
import { IFolder } from '@dataTypes/folder.types'
import { ToastComponent } from '@syncfusion/ej2-react-notifications'

// Type definition for the CustomToggle props
interface CustomToggleProps {
  children: React.ReactNode
  onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void
}

// Props interface for the component
interface ImageViewerFolderListProps {}

// The forwardRef is important!!
// Dropdown needs access to the DOM node in order to position the Menu
// eslint-disable-next-line react/display-name
const CustomToggle = React.forwardRef<HTMLAnchorElement, CustomToggleProps>(
  ({ children, onClick }, ref) => (
    <a
      ref={ref}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onClick(e)
      }}
    >
      {children}
    </a>
  ),
)

function ImageViewerFolderList({}: ImageViewerFolderListProps): React.ReactElement {
  const dispatch = useAppDispatch()
  const [showRenameModal, setShowRenameModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedFolderLocal, setSelectedFolderLocal] = useState<{
    folder_id: string
    folder_name: string
  } | null>(null)
  const toastInstance = useRef<ToastComponent>(null)

  // ToastContainer component using React Portal
  const ToastContainer = (): React.ReactElement => {
    return ReactDOM.createPortal(
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 10000,
        }}
      >
        <ToastComponent
          ref={toastInstance}
          position={{ X: 'Center', Y: 'Top' }}
          showCloseButton={false}
          showProgressBar={false}
          timeOut={3000}
        />
      </div>,
      document.body,
    )
  }

  // Get selectedPatient and folder state from Redux
  const { selectedPatient } = useAppSelector((state) => state.patient)
  const { selectedFolder, childFolders, loading, error } = useAppSelector(
    (state) => state.folder,
  )

  // Show error toast when error state changes
  // Skip showing error in development mode when using mock data (404 errors are expected)
  useEffect(() => {
    if (
      error &&
      toastInstance.current &&
      !(
        process.env.NODE_ENV === 'development' &&
        (error.includes('404') ||
          error.includes('Request failed') ||
          error.includes('Network Error'))
      )
    ) {
      let showTimeoutId: NodeJS.Timeout | null = null
      let hideTimeoutId: NodeJS.Timeout | null = null

      // Add a delay to ensure the component is fully mounted
      // Use a longer delay when error occurs after modal interactions (like delete)
      const timeoutId = setTimeout(() => {
        if (toastInstance.current) {
          try {
            // Hide any existing toasts first, but wrap in try-catch
            try {
              toastInstance.current.hide('All')
            } catch (hideErr) {
              // Ignore hide errors - component might not have any toasts yet
            }

            // Wait for hide to complete before showing new toast
            // Longer delay ensures proper state after modal closes (for delete operations)
            hideTimeoutId = setTimeout(() => {
              if (toastInstance.current) {
                showTimeoutId = setTimeout(() => {
                  if (toastInstance.current) {
                    toastInstance.current.show({
                      title: 'Error',
                      content: `Error: ${error}`,
                      cssClass: 'e-toast-danger',
                      icon: 'e-error',
                      showCloseButton: false,
                      timeOut: 3000,
                      position: { X: 'Center', Y: 'Top' },
                    })
                  }
                }, 50)
              }
            }, 100)
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('Toast error:', err)
          }
        }
      }, 150)

      return () => {
        clearTimeout(timeoutId)
        if (hideTimeoutId) {
          clearTimeout(hideTimeoutId)
        }
        if (showTimeoutId) {
          clearTimeout(showTimeoutId)
        }
      }
    }
  }, [error])

  // Check if current folder is the root folder (patient's main folder)
  const isRootFolder = (): boolean => {
    // Check if no selected folder or no patient context
    if (!selectedFolder || !selectedPatient) return true

    // If the folder has no parent_folder_id, it's a root folder
    if (!selectedFolder.parent_folder_id) return true

    // If the folder's parent_folder_id is null, it's a root folder
    if (selectedFolder.parent_folder_id === null) return true

    return false
  }

  type FolderButtonPros = {
    name: string
    noOfItems: number
    size: number
    onClick?: () => void
    onRename?: () => void
    onDelete?: () => void
    isBackToRoot?: boolean
    isSelected?: boolean
  }

  const handleRenameClick = (folder: {
    folder_id: string
    folder_name: string
  }): void => {
    setSelectedFolderLocal(folder)
    setShowRenameModal(true)
  }

  const handleRenameFolder = (newName: string): void => {
    if (selectedFolderLocal) {
      dispatch(
        renameFolderAction({
          folderId: selectedFolderLocal.folder_id,
          newName: newName,
        }),
      )
    }
  }

  const handleCloseRenameModal = (): void => {
    setShowRenameModal(false)
    setSelectedFolderLocal(null)
  }

  const handleDeleteClick = (folder: {
    folder_id: string
    folder_name: string
  }): void => {
    setSelectedFolderLocal(folder)
    setShowDeleteModal(true)
  }

  const handleDeleteFolder = (): void => {
    if (selectedFolderLocal) {
      dispatch(deleteFolder(selectedFolderLocal.folder_id))
      setShowDeleteModal(false)
      setSelectedFolderLocal(null)
    }
  }

  const handleCloseDeleteModal = (): void => {
    setShowDeleteModal(false)
    setSelectedFolderLocal(null)
  }

  const handleFolderClick = (folder: IFolder): void => {
    // Dispatch fetchFolderById to set the clicked folder as selected
    dispatch(fetchFolderById(folder.folder_id))
  }

  const handleBackToRoot = (): void => {
    // Only go back to root if we're not already at the root
    if (!isRootFolder()) {
      // If we have a selected folder and it has a parent, navigate to the parent
      if (selectedFolder && selectedFolder.parent_folder_id) {
        dispatch(fetchFolderById(selectedFolder.parent_folder_id))
      }
    }
  }

  const FolderButton = ({
    name,
    noOfItems,
    size,
    onClick,
    onRename,
    onDelete,
    isBackToRoot = false,
    isSelected = false,
  }: FolderButtonPros): React.ReactElement => {
    return (
      <div className="folder-button" onClick={onClick}>
        <div className="folder-icon-container">
          <span
            className={
              isSelected ? 'mgc_folder_open_fill' : 'mgc_folder_2_fill'
            }
          ></span>
        </div>
        <div className="folder-description-container">
          <h2>{name}</h2>
          <h3>
            {isBackToRoot
              ? ''
              : `${noOfItems} Items - ${formatBytesToMB(size)} MB`}
          </h3>
        </div>
        {!isBackToRoot && (
          <Dropdown
            className="folder-dropdown"
            onClick={(e) => e.stopPropagation()}
          >
            <Dropdown.Toggle as={CustomToggle}>
              <i className="bi bi-three-dots" style={{ color: '#8A8E97' }} />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={(e) => {
                  e.stopPropagation()
                  onRename?.()
                }}
              >
                <i
                  className="mgc mgc_pencil_fill"
                  style={{ marginRight: '8px' }}
                />
                Rename
              </Dropdown.Item>
              <Dropdown.Item
                onClick={(e) => {
                  e.stopPropagation()
                  onDelete?.()
                }}
              >
                <i
                  className="mgc mgc_delete_2_line"
                  style={{ marginRight: '8px' }}
                />
                Delete folder
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        )}
      </div>
    )
  }

  const ScrollContainer = styled.div`
    display: flex;
    width: 100%;
    justify-content: left;
    align-items: center;
    padding-bottom: 0;
  `

  // Show loading state
  if (loading) {
    return (
      <div className="folder-list">
        {/* <h1>
          {selectedPatient
            ? `${selectedPatient.first_name} ${selectedPatient.last_name}`
            : 'Loading...'}{' '}
          <span>({selectedPatient?.patient_id || '...'})</span>
        </h1>
        <div>Loading folders...</div> */}
      </div>
    )
  }

  // Error state is now handled by toast notification, no need to render error UI

  // Function to format path with spaces around forward slashes
  const formatPath = (path: string | undefined): string => {
    if (!path) return ''
    const segments = path.split('/')
    if (segments.length <= 2) return '/'

    return ` / ${segments.slice(2).join(' / ')}`
  }

  // Show no patient selected state
  if (!selectedPatient) {
    return (
      // <div className="folder-list">
      //   <h1>No Patient Selected</h1>
      //   <div>Please select a patient to view their folders.</div>
      // </div>
      <></>
    )
  }

  return (
    <div className="folder-list">
      <div className="folder-header">
        <h1>
          Folders <span>{formatPath(selectedFolder?.path)}</span>
        </h1>
      </div>

      <ScrollContainer>
        {selectedFolder ? (
          // Show contents of selected folder
          <>
            {/* Back to Root Button - only show if not at root */}
            {!isRootFolder() && (
              <FolderButton
                name="..."
                noOfItems={0}
                size={0}
                onClick={handleBackToRoot}
                isBackToRoot={true}
              />
            )}

            {/* Show subfolders - these are children of the currently selected folder */}
            {!loading &&
              childFolders &&
              childFolders.length > 0 &&
              childFolders.map((folder: IFolder) => (
                <FolderButton
                  key={folder.folder_id}
                  name={folder.folder_name}
                  noOfItems={folder.total_items || 0}
                  size={folder.folder_size || 0}
                  onClick={() => handleFolderClick(folder)}
                  onRename={() => {
                    handleRenameClick({
                      folder_id: folder.folder_id,
                      folder_name: folder.folder_name,
                    })
                  }}
                  onDelete={() => {
                    handleDeleteClick({
                      folder_id: folder.folder_id,
                      folder_name: folder.folder_name,
                    })
                  }}
                  isSelected={folder.folder_id === selectedFolder?.folder_id}
                />
              ))}
          </>
        ) : (
          // Show root folders - this will be handled by the reducer now
          <div>No folders found for this patient.</div>
        )}
      </ScrollContainer>

      <RenameFolderModal
        show={showRenameModal}
        onHide={handleCloseRenameModal}
        currentName={selectedFolderLocal?.folder_name || ''}
        onRename={handleRenameFolder}
      />

      <ConfirmBlockModal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        mainText={`Delete folder "${selectedFolderLocal?.folder_name || ''}"?`}
        subText="The folder will be deleted from the patient's list. This action cannot be undone."
        action="Delete"
        onConfirm={handleDeleteFolder}
      />

      {/* Toast Component for error notifications using React Portal */}
      <ToastContainer />
    </div>
  )
}

export default ImageViewerFolderList
